import React from 'react';
import classNames from 'classnames';

import Store from 'store';
import Data from 'data';
import Tooltip from 'ui/tooltip';
import { D3TooltipCube, D3TooltipFollowerSkill, D3TooltipItem, D3TooltipSkill } from 'tooltips';

import './Paperdoll.scss';
import 'components/paperdoll/classes.scss';

import { cubeSlotValid, cubeTypes } from 'logic/cube';
import { getAttackType } from 'logic/skills';
import { itemSlotValid } from 'logic/item';
import { getIconPath } from 'common';

function getItemIconEx(slot, profile, follower) {
  const data = (follower ? profile.followerItems[slot] : profile.items[slot]);
  if (data.id === "custom" || data.id === "customwpn") {
    const types = (slot === "offhand" ? Data.getOffhandTypes(profile, follower) : Data.itemSlots[slot].types);
    for (let type of Object.values(types)) {
      if (type.classes && type.classes.indexOf(profile.class) < 0) continue;
      if (data.id !== "customwpn" && type.weapon) continue;
      if (data.id === "customwpn" && !type.weapon) continue;
      return Data.getItemIcon(type.generic, profile);
    }
  }
  return Data.getItemIcon(data.id, profile);
}

function GemImage({ gem }) {
  if (gem.id) {
    const path = Data.getItemIcon(gem.id);
    if (path) return <img src={path} alt={Data.itemById[gem.id].name}/>;
  } else {
    const [id, color] = gem;
    if (Data.legendaryGems[id]) {
      const path = Data.getItemIcon(id);
      if (path) return <img src={path} alt={Data.legendaryGems[id].name}/>;
    } else if (Data.gemQualities[id] && Data.gemColors[color]) {
      const path = Data.getItemIcon(gem);
      if (path) return <img src={path} alt={Data.gemColors[color].names[id]}/>;
    }
  }
  return null;
}

function PaperdollSlot({ profile, slot, follower }) {
  const slotData = Data.itemSlots[slot];
  const itemData = (follower ? profile.followerItems[slot] : profile.items[slot]);
  const item = itemData && Data.itemById[itemData.id];

  if (!itemSlotValid(follower ? profile.follower : profile.class, slot)) return null;

  let disabledImage = null;
  let className = `d3p-DollSlot d3p-DragDrop d3p-slot-${slot}`;
  if (!item) {
    className += " d3p-empty";
    if (!itemData && slot === "offhand") {
      const mhData = (follower ? profile.followerItems : profile.items).mainhand;
      const mhItem = mhData && Data.itemById[mhData.id];
      if (mhItem && Data.itemTypes[mhItem.type].slot === "twohand") {
        if (!follower && profile.class === "demonhunter" && ["bow", "crossbow"].includes(mhItem.type)) {
          className += " d3p-slot-quiver";
        } else {
          className += " d3p-disabled-offhand";
          disabledImage = <img src={getItemIconEx("mainhand", profile, follower)} alt={mhItem.name}/>;
        }
      }
    }
  }

  if (item) {
    if (itemData.ancient === "sanctified") {
      className += ` d3p-quality-sanctified`;
    } else {
      className += ` d3p-quality-${item.quality}`;
      if (itemData.ancient === "primal") {
        className += " d3p-primal";
      }
    }
  }

  const node = (
    <div className={className}>
      {disabledImage}
      {!!item && <span className="d3p-ItemGradient"><span className="d3p-ItemGlow"/></span>}
      {!!item && <img src={getItemIconEx(slot, profile, follower)} alt={item.name}/>}
      {!!(item && slotData.sockets) && (
        <span className="d3p-SocketsWrapper">
          <span className="d3p-SocketsAlign">
            {!!itemData.gems && itemData.gems.map((gem, idx) => {
              return (
                <React.Fragment key={idx}>
                  <span className="d3p-Socket">
                    {!!gem && <GemImage gem={gem}/>}
                  </span>
                  <br/>
                </React.Fragment>
              );
            })}
          </span>
        </span>
      )}
    </div>
  );

  if (item) {
    return (
      <D3TooltipItem key={slot} slot={slot} follower={follower}>
        {node}
      </D3TooltipItem>
    );
  } else if (!disabledImage) {
    return (
      <Tooltip key={slot} tooltip={slotData.name} align="top">
        {node}
      </Tooltip>
    );
  } else {
    return node;
  }
}

function PaperdollGear({ follower }) {
  const profile = Store.useProfile();
  const className = follower
    ? `d3p-paperdoll-background d3p-class-${profile.follower}`
    : `d3p-paperdoll-background d3p-class-${profile.class} d3p-${profile.gender}`;
  return (
    <div className={className}>
      {Object.keys(Data.itemSlots).filter(slot => slot !== 'follower').map(slot => <PaperdollSlot key={slot} profile={profile} slot={slot} follower={follower}/>)}
    </div>
  );
}

function PaperdollSkillSlot({ index }) {
  const attackType = Store.useProfile(getAttackType);
  const charClass = Store.useProfile(profile => profile.class);
  const skill = Store.useProfile(profile => profile.skills[index]);

  const info = skill && Data.skills[charClass][skill[0]];

  let displayClass = charClass;
  let displayInfo = info;

  if (!displayInfo && (index === 0 || index === 1)) {
    displayClass = "attack";
    displayInfo = Data.skills.attack[attackType];
  }

  return (
    <D3TooltipSkill charClass={displayClass} skill={skill ? skill[0] : attackType} rune={skill?.[1]} disabled={!displayInfo}>
      <div className={classNames(`d3p-skill-icon d3p-DragDrop d3p-class-${displayClass}`, displayClass === "attack" && `d3p-${displayInfo.id}`, {"d3p-empty": !displayInfo})}
           style={(!displayInfo || displayClass === "attack") ? null : {backgroundPosition: `${-info.col}em ${-2 * info.row}em`}}>
      </div>
    </D3TooltipSkill>
  );
}

function PaperdollSkills() {
  return (
    <div className="d3p-PaperdollSkills">
      {[0, 1, 2, 3, 4, 5].map(index => <PaperdollSkillSlot key={index} index={index}/>)}
    </div>
  )
}

const passiveLevels = [10, 20, 30, 70];

function PaperdollPassiveSlot({ index }) {
  const charClass = Store.useProfile(profile => profile.class);
  const passive = Store.useProfile(profile => profile.passives[index]);

  const info = passive && Data.passives[charClass][passive];
  const empty = !info;

  return (
    <D3TooltipSkill key={index} charClass={charClass} skill={passive} disabled={empty}>
      <div key={index} className={classNames(`d3p-passive-icon d3p-DragDrop d3p-class-${charClass}`, {"d3p-empty": empty})}
          style={empty ? null : {backgroundPosition: `${-info.index}em 0`}}>
        <span className="d3p-passive-frame d3p-fancy"/>
        {empty && <span className="d3p-passive-text">{passiveLevels[index]}</span>}
      </div>
    </D3TooltipSkill>
  );
}

function PaperdollPassives() {
  return (
    <div className="d3p-PaperdollPassives">
      {[0, 1, 2, 3].map(index => <PaperdollPassiveSlot key={index} index={index}/>)}
    </div>
  )
}

function PaperdollCubeSlot({ slot }) {
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const itemId = Store.useProfile(profile => profile.kanai[slot]);

  const item = itemId && Data.itemById[itemId];

  if (!cubeSlotValid({seasonal}, slot)) return null;

  return (
    <D3TooltipCube id={item && item.id}>
      <div className={classNames(`d3p-cube-item d3p-DragDrop d3p-cube-${slot}`, {"d3p-empty": !item})}>
        {!!item && <div className="d3p-item-icon" style={{backgroundImage: `url(${Data.getItemIcon(item.id)})`}}/>}
      </div>
    </D3TooltipCube>
  );
}

function PaperdollCube() {
  return (
    <div className="d3p-PaperdollCube">
      {Object.keys(cubeTypes).map(slot => <PaperdollCubeSlot key={slot} slot={slot}/>)}
    </div>
  )
}

function PaperdollFollowerSkill({ id, active }) {
  const skill = Data.followerSkills[id];
  if (!skill) return null;

  return (
    <D3TooltipFollowerSkill id={id}>
      <div className="d3p-follower-skill" style={{backgroundImage: `url(${getIconPath(active ? skill.icon : skill.icon_inactive)})`}}/>
    </D3TooltipFollowerSkill>
  );
}

function PaperdollFollowerSkillGroup({ index }) {
  const allSkills = Store.useProfile(profile => {
    const item = profile.followerItems?.follower;
    const info = item && Data.itemById[item.id];
    return info?.required?.custom?.id === "leg_followerallskills";
  });
  const follower = Store.useProfile(profile => profile.follower);
  const selected = Store.useProfile(profile => profile.followerSkills?.[index]);

  const skill1 = Data.followerSkillList[follower]?.[index * 2];
  const skill2 = Data.followerSkillList[follower]?.[index * 2 + 1];

  return (
    <div className="d3p-follower-skill-group">
      <PaperdollFollowerSkill id={skill1} active={allSkills || skill1 === selected}/>
      <PaperdollFollowerSkill id={skill2} active={allSkills || skill2 === selected}/>
    </div>
  );
}

function PaperdollFollower() {
  const profile = Store.useProfile();
  if (!profile.follower) return null;

  return (
    <div className="d3p-PaperdollFollower">
      <div className="d3p-follower-relic">
        <PaperdollSlot profile={profile} slot="follower" follower={true}/>
      </div>
      <div className="d3p-follower-skills">
        <PaperdollFollowerSkillGroup index={0}/>
        <PaperdollFollowerSkillGroup index={1}/>
        <PaperdollFollowerSkillGroup index={2}/>
        <PaperdollFollowerSkillGroup index={3}/>
      </div>
    </div>
  );
}

export default React.memo(function Paperdoll({ style, href }) {
  const charClass = Store.useProfile(profile => profile.class);
  const followerClass = Store.useProfile(profile => profile.follower);
  const hasFollower = !!followerClass;
  const hasCharacter = !!Store.useProfile(profile => Object.values(profile.items).some(Boolean)) || !hasFollower;

  const [mode, setMode] = React.useState(hasCharacter ? "character" : "follower");
  const onSetCharacter = React.useCallback(() => setMode("character"), []);
  const onSetFollower = React.useCallback(() => setMode("follower"), []);

  const follower = (mode === "follower");

  return (
    <div className={"d3p-EmbedContainer"} style={style}>
      <div className="d3p-mode-toggle">
        {hasCharacter && <div className={classNames("d3p-mode", {"d3p-active": mode === "character"})} onClick={onSetCharacter}>
          <span className={`d3p-class-icon d3p-class-${charClass}`}/> <span className="d3p-name">{Data.classes[charClass].name}</span>
        </div>}
        {hasFollower && <div className={classNames("d3p-mode", {"d3p-active": mode === "follower"})} onClick={onSetFollower}>
          <span className={`d3p-class-icon d3p-class-${followerClass}`}/> <span className="d3p-name">{Data.classes[followerClass].name}</span>
        </div>}
      </div>
      <a className="d3p-PaperdollContainer" href={href} target="_blank" rel="noopener noreferrer">
        <PaperdollGear follower={follower}/>
        {!follower && <PaperdollSkills/>}
        {!follower && <PaperdollPassives/>}
        {!follower && <PaperdollCube/>}
        {follower && <PaperdollFollower/>}
      </a>
    </div>
  );
});
